import React, { useEffect, useState, useCallback } from "react";
import {
  Container,
  AppBar,
  Toolbar,
  Box,
  Typography,
  List,
  ListItem,
  CircularProgress,
  IconButton,
  Button,
  Paper,
  Avatar,
  styled,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { isLoggedIn } from "../session";
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  updateDoc,
  doc,
  orderBy,
  limit,
  onSnapshot,
  writeBatch,
} from "firebase/firestore";
import { formatDistanceToNow, isToday, isYesterday } from "date-fns";
import { es } from "date-fns/locale";
import BottomNav from "./BottomNav";
import Atras from "./atras";
import {
  Refresh as RefreshIcon,
  Chat as ChatIcon,
  Work as WorkIcon,
  Description as DescriptionIcon,
  Notifications as NotificationsIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  DoneAll as DoneAllIcon,
} from "@mui/icons-material";

// Estilos personalizados
const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  boxShadow: theme.shadows[1],
}));

const NotificationPaper = styled(Paper)(({ theme, read }) => ({
  width: '100%',
  marginBottom: theme.spacing(1.5),
  padding: theme.spacing(2),
  borderRadius: 14,
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderLeft: `3px solid ${read ? 'transparent' : theme.palette.primary.main}`,
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
    cursor: 'pointer',
  },
}));

const SectionLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: theme.palette.text.secondary,
  margin: theme.spacing(2, 0, 1, 0.5),
}));

const NotificationAvatar = styled(Avatar)(({ theme, notificationType }) => {
  const colors = {
    chat: theme.palette.primary.main,
    project_skill: theme.palette.success.main,
    new_proposal: theme.palette.secondary.main,
    proposal_accepted: theme.palette.success.main,
    proposal_rejected: theme.palette.error.main,
  };

  return {
    backgroundColor: colors[notificationType] || theme.palette.grey[500],
  };
});

const LoadMoreButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(2, 0),
}));

const EmptyStateBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  height: '50vh',
}));

const NOTIFICATIONS_PER_PAGE = 10;

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  const fetchNotifications = useCallback(async () => {
    let unsubscribe = null;
    try {
      const user = await isLoggedIn();
      if (!user) {
        navigate("/login");
        return null;
      }

      const uid = user.uid;

      const notificationsRef = collection(db, "notifications");
      const notificationsQuery = query(
        notificationsRef,
        where("userId", "==", uid),
        orderBy("timestamp", "desc"),
        limit(page * NOTIFICATIONS_PER_PAGE)
      );

      unsubscribe = onSnapshot(notificationsQuery, (snapshot) => {
        try {
          const notificationsData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setNotifications(notificationsData);
          setError(null);
        } catch (error) {
          console.error("Error processing notifications update:", error);
          setError("Failed to process notifications update. Please try again.");
        } finally {
          setLoading(false);
        }
      }, (error) => {
        console.error("Error in notifications listener:", error);
        setError("Failed to listen for notifications. Please try again.");
        setLoading(false);
      });

    } catch (err) {
      console.error("Error fetching notifications:", err);
      setError("Failed to load notifications. Please try again.");
      setLoading(false);
    }

    return unsubscribe;
  }, [navigate, page]);

  useEffect(() => {
    let unsubscribe = null;

    const setupNotifications = async () => {
      unsubscribe = await fetchNotifications();
    };

    setupNotifications();

    return () => {
      if (unsubscribe && typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [fetchNotifications]);

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'chat':
        return <ChatIcon />;
      case 'project_skill':
        return <WorkIcon />;
      case 'new_proposal':
        return <DescriptionIcon />;
      case 'proposal_accepted':
        return <CheckCircleIcon />;
      case 'proposal_rejected':
        return <CancelIcon />;
      default:
        return <NotificationsIcon />;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'chat':
        return 'primary';
      case 'project_skill':
        return 'success';
      case 'new_proposal':
        return 'secondary';
      case 'proposal_accepted':
        return 'success';
      case 'proposal_rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleNotificationClick = async (notification) => {
    try {
      const notificationRef = doc(db, "notifications", notification.id);
      await updateDoc(notificationRef, { read: true });

      switch (notification.type) {
        case "chat":
          navigate(`/chatbox/${notification.projectId}/${notification.proposalId}/${notification.userId || ""}/${notification.projectUid}`);
          break;
        case "project_skill":
          navigate(`/project/${notification.projectId}`);
          break;
        case "new_proposal":
          navigate(`/project/${notification.projectId}/proposals/${notification.proposalId}`);
          break;
        case "proposal_accepted":
        case "proposal_rejected":
          navigate(`/propuestas`);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error("Error updating notification:", error);
      setError("Failed to update notification. Please try again.");
    }
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  const handleMarkAllRead = async () => {
    const unread = notifications.filter((n) => !n.read);
    if (unread.length === 0) return;
    try {
      const batch = writeBatch(db);
      unread.forEach((n) => batch.update(doc(db, "notifications", n.id), { read: true }));
      await batch.commit();
    } catch (error) {
      console.error("Error marking all as read:", error);
      setError("No se pudieron marcar como leídas. Intentá de nuevo.");
    }
  };

  const groupByDay = (list) => {
    const groups = { Hoy: [], Ayer: [], "Esta semana": [], Anteriores: [] };
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);

    list.forEach((n) => {
      const date = n.timestamp?.toDate ? n.timestamp.toDate() : null;
      if (!date) {
        groups.Anteriores.push(n);
      } else if (isToday(date)) {
        groups.Hoy.push(n);
      } else if (isYesterday(date)) {
        groups.Ayer.push(n);
      } else if (date > weekAgo) {
        groups["Esta semana"].push(n);
      } else {
        groups.Anteriores.push(n);
      }
    });

    return Object.entries(groups).filter(([, items]) => items.length > 0);
  };

  const renderNotificationContent = (notification) => {
    const color = getNotificationColor(notification.type);

    return (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <NotificationAvatar notificationType={notification.type}>
          {getNotificationIcon(notification.type)}
        </NotificationAvatar>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="subtitle1"
              color={`${color}.main`}
              sx={{ fontWeight: 600 }}
            >
              {notification.type === 'chat' && 'Nuevo mensaje de chat'}
              {notification.type === 'project_skill' && 'Nuevo proyecto coincidente'}
              {notification.type === 'new_proposal' && 'Nueva propuesta recibida'}
              {notification.type === 'proposal_accepted' && 'Propuesta aceptada'}
              {notification.type === 'proposal_rejected' && 'Propuesta rechazada'}
            </Typography>
            {!notification.read && (
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main', flexShrink: 0 }} />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {notification.message}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {notification.timestamp?.toDate &&
              formatDistanceToNow(notification.timestamp.toDate(), { addSuffix: true, locale: es })}
          </Typography>
        </Box>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="sm">
      <StyledAppBar position="fixed">
        <Toolbar>
          <Atras color="inherit" />
          <Typography variant="h6" sx={{ flexGrow: 1, textAlign: 'center' }}>
            Notificaciones{unreadCount > 0 ? ` (${unreadCount})` : ""}
          </Typography>
          {unreadCount > 0 && (
            <IconButton color="inherit" onClick={handleMarkAllRead} title="Marcar todas como leídas">
              <DoneAllIcon />
            </IconButton>
          )}
          <IconButton color="inherit" onClick={() => {
            setLoading(true);
            setPage(1);
            fetchNotifications();
          }}>
            <RefreshIcon />
          </IconButton>
        </Toolbar>
      </StyledAppBar>

      <Box sx={{ mt: 10, mb: 10 }}>
        {error && (
          <Typography color="error" align="center" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
        
        
        {notifications.length > 0 ? (
          <>
            {groupByDay(notifications).map(([label, items]) => (
              <Box key={label}>
                <SectionLabel>{label}</SectionLabel>
                <List sx={{ width: '100%' }} disablePadding>
                  {items.map((notification) => (
                    <ListItem
                      key={notification.id}
                      disablePadding
                    >
                      <NotificationPaper
                        read={notification.read}
                        onClick={() => handleNotificationClick(notification)}
                        elevation={0}
                      >
                        {renderNotificationContent(notification)}
                      </NotificationPaper>
                    </ListItem>
                  ))}
                </List>
              </Box>
            ))}

            {notifications.length % NOTIFICATIONS_PER_PAGE === 0 && (
              <LoadMoreButton
                fullWidth
                variant="outlined"
                color="primary"
                onClick={() => setPage(prev => prev + 1)}
              >
                Cargar más
              </LoadMoreButton>
            )}
          </>
        ) : (
          <EmptyStateBox>
            <NotificationsIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No tienes notificaciones nuevas
            </Typography>
          </EmptyStateBox>
        )}
      </Box>

      <BottomNav />
    </Container>
  );
};

export default Notifications;